import {APP_BASE_HREF, CommonModule, LocationStrategy, PathLocationStrategy} from '@angular/common';
import {HttpClientModule} from '@angular/common/http';
import {ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {RouterModule, Routes} from '@angular/router';
import * as Sentry from '@sentry/angular';
import {AppComponent} from '@app/app.component';
import {BootstrapServiceBase} from '@app/bootstrap.service.base';
import {BootstrapService} from '@app/bootstrap.service';
import {OvenConnectionManagerService} from '@app/connect/oven-connection-manager.service';
import {BakeEndConditionService} from '@app/event/bake-end-condition.service';
import {EventTriggerService, SIQ_EVENT_CONDITION_PROVIDER} from '@app/event/event-trigger.service';
import {ScheduleConditionService} from '@app/event/schedule-condition.service';
import {IntroPageComponent} from '@app/platform/intro-page.component';
import {AbstractPlatform} from '@app/platform/platform';
import {WebPlatform} from '@app/platform/platform-web';
import {EventSchedulerService} from '@app/player/event-scheduler.service';
import {PlayerModule} from '@app/player/player.module';
import {PlaylistPlayerComponent} from '@app/player/playlist-player.component';
import {SchedulerServiceBase} from '@app/player/scheduler.service.base';
import {RegisterService} from '@app/setup/register.service';
import {SharedModule} from '@app/shared.module';
import {ActionService} from '@app/shared/action.service';
import {ControllerClientServiceBase} from '@app/shared/controller-client.service.base';
import {ControllerClientService} from '@app/shared/controller-client.service';
import {LoggingModule} from '@app/shared/logging.module';
import {TimeService} from '@app/shared/time.service';
import {TimeServiceBase} from '@app/shared/time.service.base';
import {FileSyncServiceBase} from '@app/sync/file-sync.service.base';
import {FileSyncService} from '@app/sync/file-sync.service';
import {SettingsService} from '@app/sync/settings.service';
import {SyncLoaderPageComponent} from '@app/sync/sync-loader-page.component';
import {SyncServiceBase} from '@app/sync/sync.service.base';
import {SyncService} from '@app/sync/sync.service';

const appRoutes: Routes = [
    {path: 'init/:secret', component: IntroPageComponent},
    {path: 'sync', component: SyncLoaderPageComponent},
    {path: 'player', component: PlaylistPlayerComponent},
    {path: 'setup' , redirectTo: 'sync'},
    {path: '', component: IntroPageComponent},
    {path: '*', redirectTo: ''},
];

@NgModule({
    declarations: [
        // all declarations are sorted alphabetically
        AppComponent,
    ],
    imports: [
        BrowserModule,
        CommonModule,
        FormsModule,
        HttpClientModule,
        RouterModule.forRoot(appRoutes, {initialNavigation: 'enabled'}),
        LoggingModule,
        SharedModule,
        PlayerModule,
    ],
    providers: [
        {provide: APP_BASE_HREF, useValue: '/'},
        {provide: LOCALE_ID, useValue: 'de-DE'},
        {provide: LocationStrategy, useClass: PathLocationStrategy},
        {provide: ErrorHandler, useValue: Sentry.createErrorHandler()},
        // Currently only the tizen and web platform is available in non-nativescript project
        {provide: AbstractPlatform, useClass: WebPlatform},
        // This is needed to circumvent circular import issues
        {provide: SyncServiceBase, useExisting: SyncService},
        {provide: SIQ_EVENT_CONDITION_PROVIDER, useClass: BakeEndConditionService, multi: true},
        {provide: SIQ_EVENT_CONDITION_PROVIDER, useClass: ScheduleConditionService, multi: true},
        {provide: BootstrapServiceBase, useExisting: BootstrapService},
        {provide: SyncServiceBase, useExisting: SyncService},
        {provide: FileSyncServiceBase, useExisting: FileSyncService},
        {provide: ControllerClientServiceBase, useExisting: ControllerClientService},

        ControllerClientService,
        // all other providers are sorted alphabetically
        ActionService,
        BootstrapService,
        EventSchedulerService,
        EventTriggerService,
        FileSyncService,
        OvenConnectionManagerService,
        RegisterService,
        SchedulerServiceBase,
        SettingsService,
        SyncService,
        TimeService,
        {provide: TimeServiceBase, useExisting: TimeService},
    ],
    bootstrap: [AppComponent],
})
export class AppModule {
}
